import type { components } from '@connected/api-client/api.generated';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { Switch } from '@connected/form-fields-v2/components/form/switch';
import { useAppForm } from '@connected/form-fields-v2/hooks/use-app-form';
import { Button } from '@connected/ui/components/button';
import { Popout, PopoutBody, PopoutContent, PopoutHeader, PopoutTitle } from '@connected/ui/components/popout';
import { Skeleton } from '@connected/ui/components/skeleton';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, PlusIcon, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import z from 'zod';

import { DataTable } from '@/components/data-table';
import { ExpandedRowsMobile } from '@/components/expanded-row';
import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';

export const Route = createFileRoute(
    '/_auth/dashboard/organizations/$organizationId/settings/templates/',
)({
    beforeLoad: async ({ context, params }) => {
        if (context?.currentUser?.IsAdmin !== true) {
            throw redirect({
                to: '/dashboard/organizations/$organizationId/settings',
                params,
            });
        }
    },
    component: RouteComponent,
});

type templateFile = components['schemas']['ListDocumentTemplateQueryResultTemplate'];
type listTemplateFiles = components['schemas']['ListDocumentTemplateQueryResultTemplate'];

function CreateTemplate(
    {
        isOpen,
        setIsOpen,
    }: {
        isOpen: boolean;
        setIsOpen: (isOpen: boolean) => void;
    },
) {
    const params = Route.useParams();
    const { t } = useTranslation('dashboard');

    const addTemplate = api.useMutation('post', '/api/v2/documents/templates/create');

    const templateValues = useMemo(() => ([
        {
            label: 'Scope 8',
            value: 'ElectricalProjectScope8Report',
            description: 'Scope 8 template',
        },
        {
            label: 'Scope 10',
            value: 'ElectricalProjectScope10Report',
            description: 'Scope 10 template',
        },
        {
            label: 'Scope 12',
            value: 'ElectricalProjectScope12Report',
            description: 'Scope 12 template',
        },
        {
            label: 'NEN 1010 Report',
            value: 'ElectricalProjectNen1010Report',
            description: 'NEN 1010 template',
        },
        {
            label: 'NEN 3140 Report',
            value: 'ElectricalProjectNen3140Report',
            description: 'NEN 3140 template',
        },
        {
            label: 'Offerte',
            value: 'ElectricalProjectPriceQuote',
            description: 'Offerte template',
        },
        {
            label: 'Inspectieplan',
            value: 'ElectricalProjectInspectionPlan',
            description: 'Inspectieplan template',
        },
        {
            label: 'Stroomonderbrekingsplan',
            value: 'ElectricalProjectPowerShutdownPlan',
            description: 'Stroomonderbrekingsplan template description',
        },
        {
            label: 'Ernstige melding',
            value: 'ElectricalProjectSeriousDefectReport',
            description: 'Ernstige melding template',
        },
    ]), []);

    const DEFAULT_VALUES: components['schemas']['CreateDocumentTemplateCommand'] = {
        Name: '',
        Description: '',
        PdfKind: null!,
        GroupName: '',
        IntervalKind: 'None',
        OrganizationId: params.organizationId,
    };

    const intervals = [
        { label: 'None', value: 'None' },
        { label: 'First', value: 'First' },
        { label: 'Repeat', value: 'Repeat' },
    ];

    const form = useAppForm(
        {
            defaultValues: DEFAULT_VALUES,
            onSubmit: async ({ value }) => {
                await addTemplate.mutateAsync({
                    body: value,
                }).catch((error) => {
                    toast.error(error?.Message || t('dashboard.organizations.templates.createTemplate.errorToast'));
                });
                setIsOpen(false);
                queryClient.invalidateQueries({
                    queryKey: elektraAppQueryOptions.organisation.management.documentTemplatesOptions({
                        organisationId: params.organizationId,
                    }).queryKey,
                });
            },
        },
    );

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        form.handleSubmit();
    }

    const setDefaultValuesForTemplate = useCallback((templateType: string) => {
        form.setFieldValue('Name', templateValues.find(t => t.value === templateType)?.label || '');
        form.setFieldValue('Description', templateValues.find(t => t.value === templateType)?.description || '');
    }, [form, templateValues]);

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>{t('dashboard.organizations.templates.createTemplate.title')}</PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
                        <form.Subscribe
                            selector={state => state.values.PdfKind}
                            children={(pdfKind) => {
                                if (pdfKind) {
                                    setDefaultValuesForTemplate(pdfKind as string);
                                }
                                return null;
                            }}
                        />
                        <form.AppField
                            name="PdfKind"
                            validators={{
                                onChange: z.enum(['ElectricalProjectScope8Report', 'ElectricalProjectScope10Report', 'ElectricalProjectScope12Report', 'ElectricalProjectNen1010Report', 'ElectricalProjectNen3140Report', 'ElectricalProjectPriceQuote', 'ElectricalProjectInspectionPlan', 'ElectricalProjectPowerShutdownPlan', 'ElectricalProjectSeriousDefectReport']),
                            }}
                            children={field => (
                                <field.Label asDiv>
                                    <field.Label.Title label="Soort template" />
                                    <field.Popover closeOnChange={true}>
                                        <field.RadioGroup
                                            options={templateValues}
                                        >
                                            <field.Popover.Trigger asChild>
                                                <field.RadioGroup.Trigger placeholder={t('dashboard.users.table.rolePlaceholder')} />
                                            </field.Popover.Trigger>
                                            <field.Popover.Content className="h-auto min-h-30 [&>div]:flex-col [&>div]:items-start [&>div]:gap-6" matchTriggerWidth>
                                                <field.RadioGroup
                                                    options={templateValues}
                                                />
                                            </field.Popover.Content>
                                        </field.RadioGroup>
                                    </field.Popover>
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />
                        <div>
                            <form.AppField
                                name="Name"
                                validators={{
                                    onChange: validators.createDocumentTemplateCommand.name,
                                }}
                                children={field => (
                                    <field.Label asDiv>
                                        <field.Label.Title label={t('dashboard.organizations.templates.table.name')} />
                                        <field.Input placeholder={t('dashboard.organizations.templates.table.namePlaceholder')} />
                                        <field.ValidationError />
                                    </field.Label>
                                )}
                            />
                            <form.AppField
                                name="Description"
                                validators={{
                                    onChange: validators.createDocumentTemplateCommand.description,
                                }}
                                children={field => (
                                    <field.Label asDiv>
                                        <field.Label.Title label={t('dashboard.organizations.templates.table.description')} />
                                        <field.Input placeholder={t('dashboard.organizations.templates.table.descriptionPlaceholder')} />
                                        <field.ValidationError />
                                    </field.Label>
                                )}
                            />
                            <form.AppField
                                name="GroupName"
                                validators={{
                                    onChange: validators.createDocumentTemplateCommand.groupName,
                                }}
                                children={field => (
                                    <field.Label asDiv>
                                        <field.Label.Title label={t('dashboard.organizations.templates.table.groupName')} />
                                        <field.Input placeholder={t('dashboard.organizations.templates.table.groupNamePlaceholder')} />
                                        <field.ValidationError />
                                    </field.Label>
                                )}
                            />
                            <form.AppField
                                name="IntervalKind"
                                validators={{
                                    onChange: validators.createDocumentTemplateCommand.intervalKind,
                                }}
                                children={field => (
                                    <field.Label asDiv>
                                        <field.Label.Title label={t('dashboard.organizations.templates.table.intervalKind')} />
                                        <field.Popover closeOnChange={true}>
                                            <field.RadioGroup
                                                options={intervals}
                                            >
                                                <field.Popover.Trigger asChild>
                                                    <field.RadioGroup.Trigger placeholder={t('dashboard.users.table.rolePlaceholder')} />
                                                </field.Popover.Trigger>
                                                <field.Popover.Content className="h-auto min-h-30 [&>div]:flex-col [&>div]:items-start [&>div]:gap-6" matchTriggerWidth>
                                                    <field.RadioGroup
                                                        options={intervals}
                                                    />
                                                </field.Popover.Content>
                                            </field.RadioGroup>
                                        </field.Popover>
                                        <field.ValidationError />
                                    </field.Label>
                                )}
                            />
                        </div>
                        <div className="w-full flex flex-row gap-2">
                            <Button className="w-full" onClick={() => setIsOpen(false)} variant="outline">{t('dashboard.cancel')}</Button>
                            <Button type="submit" variant="default" className="w-full">{t('dashboard.create')}</Button>
                        </div>
                    </form>
                </PopoutBody>
            </PopoutContent>
        </Popout>
    );
}

function RouteComponent() {
    const params = Route.useParams();
    const { t } = useTranslation('dashboard');
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    const templates = useQuery({ ...elektraAppQueryOptions.organisation.management.documentTemplatesOptions({
        organisationId: params.organizationId,
    }) });

    const deleteTemplate = api.useMutation('delete', '/api/v2/documents/templates/delete');

    const columns = useMemo(() => [
        {
            accessorKey: 'Name',
            header: () => (
                <DataTable.Header>
                    {t('dashboard.organizations.table.headers.name')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: templateFile } }) => (
                <DataTable.Cell variant="bold">
                    <Button
                        variant="ghost"
                        className="sm:hidden mr-2"
                        onClick={() => setExpandedId(prev => prev === row?.original?.DocumentTemplateId ? null : row?.original?.DocumentTemplateId ?? null)}
                    >
                        {expandedId === row?.original?.DocumentTemplateId ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </Button>
                    {row?.original?.Name}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'TypeInspection',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.projects.documents.table.inspectionKind')}
                </DataTable.Header>
            ),
            cell: () => (
                <DataTable.Cell variant="bold">

                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Description',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.organizations.table.headers.description')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: templateFile } }) => (
                <DataTable.Cell variant="bold">
                    {row?.original?.Description}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'LastEdit',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.settings.reports.table.lastEdited')}
                </DataTable.Header>
            ),
            cell: () => (
                <DataTable.Cell variant="bold">

                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'onOffButton',
            header: () => (
                <DataTable.Header>
                    {t('dashboard.settings.reports.table.onOffButton')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: templateFile } }) => (
                <DataTable.Cell variant="bold">
                    <Switch
                        name={`switch-${row?.original?.DocumentTemplateId ?? ''}`}
                        value={row?.original?.Enabled ?? false}
                        onChange={() => {

                        }}
                    />
                </DataTable.Cell>
            ),
        },
        {
            id: 'actionModal',
            meta: {
                sticky: true,
                width: 10,
            },
            header: '',
            cell: ({ row }: { row: { original: templateFile } }) => {
                return (
                    <div className="flex gap-1 flex-col sm:flex-row items-center justify-end ml-auto">
                        <Link
                            to="/dashboard/organizations/$organizationId/settings/templates/$templateId"
                            params={{
                                organizationId: params.organizationId,
                                templateId: row.original.DocumentTemplateId ?? '',
                            }}
                        >
                            <Button
                                variant="secondary"
                                size="icon"
                                className="flex gap-2 underline w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                            >
                                {t('dashboard.overview')}
                            </Button>
                        </Link>
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                            onClick={async () => {
                                if (await showConfirm('Weet je zeker dat je de template wilt verwijderen?')) {
                                    await deleteTemplate.mutateAsync({
                                        params: {
                                            query: {
                                                DocumentTemplateId: row.original.DocumentTemplateId ?? '',
                                            },
                                        },
                                    });
                                    await queryClient.invalidateQueries({ ...elektraAppQueryOptions.organisation.management.documentTemplatesOptions({
                                        organisationId: params.organizationId,
                                    }) });
                                }
                            }}
                        >
                            <Trash2 className="text-text-primary/40 size-5" />
                        </Button>
                    </div>
                );
            },
        },
    ], [deleteTemplate, expandedId, params.organizationId, t]);

    const tableColumns = useMemo(
        () => templates.isLoading
            ? columns.map(column => ({ ...column, cell: () => <Skeleton className="h-8 w-full" /> }))
            : columns,
        [columns, templates.isLoading],
    );

    const tableData = useMemo<listTemplateFiles[]>(
        () => {
            if (templates.isError) {
                toast.error(templates.error?.Message);
                return [];
            }
            const searchTerm = '';

            return templates.data?.Templates?.filter((norm) => {
                if (!searchTerm)
                    return true;

                return [
                    norm.Name || '',
                ].some(field => field?.toString().toLowerCase().includes(searchTerm));
            }) || [];
        },
        [templates.isError, templates.data, templates.error?.Message],
    );

    return (
        <div>
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-9">
                <div className="w-full">
                    <DataTable.TableHead
                        title={t('dashboard.settings.reports.title')}
                        subtitle={t('dashboard.settings.reports.subtitle')}
                        buttons={[
                            {
                                name: 'Template',
                                icon: <PlusIcon size={19} />,
                                callback: () => {
                                    setIsOpen(true);
                                },
                            },
                        ]}
                    />
                    <DataTable
                        className="rounded-tl-none rounded-tr-none"
                        columns={tableColumns}
                        data={tableData}
                        pageSize={10}
                        expandedRow={row =>
                            expandedId && expandedId === row?.original?.DocumentTemplateId
                                ? (
                                        <ExpandedRowsMobile columns={columns} data={row.original} />
                                    )
                                : null}
                    />
                </div>
            </div>
            {isOpen && (
                <CreateTemplate isOpen={isOpen} setIsOpen={setIsOpen} />
            )}
        </div>
    );
}
